import React, { useId, useState } from "react";
import { Email, CheckCircle } from "@mui/icons-material";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { addSubscriber } from "../utils/subscriptionUtils";

interface SubscribeFormProps {
  compact?: boolean;
}

export default function SubscribeForm({ compact = false }: SubscribeFormProps) {
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setLoading(true);
      setError(null);

      try {
        const result = await addSubscriber(email);

        if (result.success) {
          setSubscribed(true);
          setEmail("");
        } else {
          setError(result.error || "Failed to subscribe. Please try again.");
        }
      } catch (err) {
        console.error("Error subscribing:", err);
        setError("An unexpected error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  if (compact) {
    return (
      <section aria-label="Subscribe to new essays">
        <p className="mb-3 text-sm leading-relaxed">New essays, occasionally.</p>
        {subscribed ? (
          <p role="status" className="text-sm leading-relaxed text-primary-700 dark:text-primary-300">
            Thanks for subscribing. You’re on the list.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2" aria-busy={loading}>
            <label htmlFor={emailId} className="sr-only">Email address</label>
            <input
              id={emailId}
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Your email address"
              required
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${emailId}-error` : undefined}
              className="min-h-11 min-w-0 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-950 placeholder:text-neutral-500 disabled:opacity-50 dark:border-neutral-600 dark:bg-neutral-950 dark:text-neutral-100"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex min-h-11 items-center justify-center gap-1 rounded-sm bg-primary-600 px-3 py-2 text-sm text-white transition-colors hover:bg-primary-700 disabled:cursor-wait disabled:opacity-60 dark:bg-primary-500 dark:hover:bg-primary-600"
            >
              {loading ? "Joining…" : <>Subscribe <ArrowForward sx={{ fontSize: 16 }} /></>}
            </button>
          </form>
        )}
        {error && <p role="alert" id={`${emailId}-error`} className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-2xl border border-neutral-200/80 bg-white/82 p-6 shadow-[0_18px_70px_rgba(23,23,23,0.06)] backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/70 dark:shadow-none sm:p-8">
      <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-primary-200/34 blur-3xl dark:bg-primary-700/18" />
      <div className="relative mb-6 flex items-start gap-4">
        <div className="rounded-2xl bg-primary-600 p-3 text-white dark:bg-primary-500">
          <Email className="w-5 h-5 text-white flex-shrink-0" />
        </div>
        <div>
          <h3 className="text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50">
            Get the next essay
          </h3>
          <p className="mt-2 max-w-lg text-sm leading-6 text-neutral-600 dark:text-neutral-400">
            A short note when a new piece is published. No feed noise.
          </p>
        </div>
      </div>

      {error && (
        <div className="relative mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 animate-slide-up dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      {subscribed ? (
        <div className="relative flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 animate-slide-up dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">
            Thanks for subscribing! You’re on the list.
          </span>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="relative grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
        >
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
              Email address
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-neutral-950 transition-smooth placeholder:text-neutral-500 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500"
              required
              disabled={loading}
            />
          </label>
          <button
            type="submit"
            className="rounded-full bg-neutral-950 px-6 py-3 font-semibold text-white shadow-sm transition-smooth hover:bg-primary-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-50 dark:text-neutral-950 dark:hover:bg-primary-200 whitespace-nowrap"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Subscribing...
              </span>
            ) : (
              "Subscribe"
            )}
          </button>
        </form>
      )}
    </section>
  );
}
